/**
 * HiddenFieldErrors — validation errors on fields the form validates but does not render.
 *
 * With `hideIdentity`, the add flow names the account above the form, so the form drops its own
 * identity fields while still validating their prefilled values. An error there has no `FormMessage`
 * to show it in, and Test would do nothing visible. This Callout names each such field and its error.
 */
import * as React from 'react';
import { type Control, type FieldPath, type FieldValues, useFormState } from 'react-hook-form';

import { Callout } from '@/_shared/components/ui/callout';

interface HiddenFieldErrorsProps<T extends FieldValues> {
  control: Control<T>;
  /** The hidden fields, in display order, with the label each error is shown under. */
  fields: readonly { name: FieldPath<T>; label: string }[];
}

export function HiddenFieldErrors<T extends FieldValues>({
  control,
  fields,
}: HiddenFieldErrorsProps<T>): React.JSX.Element | null {
  const { errors } = useFormState({ control, name: fields.map((f) => f.name) });
  const messages = fields.flatMap(({ name, label }) => {
    const message = (errors as Record<string, { message?: unknown } | undefined>)[name]?.message;
    return typeof message === 'string' && message ? [`${label}: ${message}`] : [];
  });
  if (messages.length === 0) return null;
  return (
    <Callout variant="error" role="alert">
      {messages.map((m) => (
        <p key={m}>{m}</p>
      ))}
    </Callout>
  );
}
